import { FormEvent, useMemo, useState } from "react";
import { RECORD_TYPES, type RecordType } from "../lib/recordTypes";
import { checkTracker, type TrackerReport, type TrackerRow, type TrackerState } from "../lib/tracker";

const STATE_LABEL: Record<TrackerState, string> = {
  old: "Old",
  new: "New",
  mixed: "Mixed",
  other: "Other",
  missing: "Missing",
  failed: "Failed",
};

export function DnsChangeTracker() {
  const initial = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      host: p.get("host") ?? "epochcalc.com",
      type: normalizeType(p.get("type") ?? "A"),
      oldValue: p.get("old") ?? "",
      newValue: p.get("new") ?? "",
    };
  }, []);
  const [host, setHost] = useState(initial.host);
  const [type, setType] = useState<RecordType>(initial.type);
  const [oldValue, setOldValue] = useState(initial.oldValue);
  const [newValue, setNewValue] = useState(initial.newValue);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<TrackerReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setReport(null);
    setError(null);
    const cleanHost = host.trim().toLowerCase().replace(/\.$/, "");
    const params = new URLSearchParams({ host: cleanHost, type, old: oldValue.trim(), new: newValue.trim() });
    history.replaceState(null, "", `?${params.toString()}`);
    checkTracker({ host: cleanHost, type, oldValue, newValue })
      .then((next) => setReport(next))
      .catch((err) => setError(String(err instanceof Error ? err.message : err)))
      .finally(() => setBusy(false));
  };

  return (
    <main className="console tracker-page">
      <section className="tracker-hero">
        <span className="landing-eyebrow">DNS change tracker</span>
        <h1>See who still has the old answer.</h1>
        <p className="landing-lede">
          Compare an old DNS value against the new one across 14 public resolvers, then see exactly which networks have switched.
        </p>
      </section>

      <form className="tracker-form" onSubmit={submit}>
        <label className="watch-field tracker-host">
          <span className="gen-label">Domain or hostname</span>
          <input value={host} onChange={(e) => setHost(e.target.value)} placeholder="example.com" spellCheck={false} autoCapitalize="off" autoCorrect="off" />
        </label>
        <label className="watch-field">
          <span className="gen-label">Record type</span>
          <select value={type} onChange={(e) => setType(e.target.value as RecordType)}>
            {RECORD_TYPES.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>
        <label className="watch-field">
          <span className="gen-label">Old value</span>
          <textarea value={oldValue} onChange={(e) => setOldValue(e.target.value)} placeholder="Old IP, MX host, TXT value, or CNAME" rows={3} />
        </label>
        <label className="watch-field">
          <span className="gen-label">New value</span>
          <textarea value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="New IP, MX host, TXT value, or CNAME" rows={3} />
        </label>
        <button type="submit" className="watch-submit" disabled={busy || !host.trim() || !oldValue.trim() || !newValue.trim()}>
          {busy ? "Checking..." : "Compare across resolvers"}
        </button>
      </form>

      {error ? <div className="error" role="alert">{error}</div> : null}

      <TrackerAlertForm host={host} type={type} oldValue={oldValue} newValue={newValue} />

      {report ? (
        <section className={`tracker-results tracker-${report.summary.tone}`}>
          <div className="tracker-summary">
            <span className="answer-eyebrow">Live comparison</span>
            <h2>{report.summary.title}</h2>
            <p>{report.summary.detail}</p>
          </div>
          <div className="tracker-counts" aria-label="Resolver state counts">
            {(["new", "old", "mixed", "other", "missing", "failed"] as TrackerState[]).map((state) => (
              <div key={state} className={`tracker-count tracker-state-${state}`}>
                <b>{report.counts[state]}</b>
                <span>{STATE_LABEL[state]}</span>
              </div>
            ))}
          </div>
          <TrackerTable rows={report.rows} />
        </section>
      ) : null}
    </main>
  );
}

function TrackerAlertForm({ host, type, oldValue, newValue }: { host: string; type: RecordType; oldValue: string; newValue: string }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const ready = host.trim() && oldValue.trim() && newValue.trim();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const res = await fetch("/api/watch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          host: normalizeHost(host),
          type,
          mode: "propagation",
          oldValue,
          newValue,
        }),
      });
      const body = (await res.json()) as { ok?: boolean; message?: string };
      setResult({ ok: !!body.ok, message: String(body.message ?? "Something went wrong.") });
      if (body.ok) setEmail("");
    } catch {
      setResult({ ok: false, message: "Couldn't reach the server. Try again in a moment." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="tracker-alert">
      <div>
        <span className="answer-eyebrow">Optional alert</span>
        <h2>Email me when this finishes</h2>
        <p>
          Get one confirmed email when every resolver returns the new value, or if the tracker sees an unexpected answer.
        </p>
      </div>
      <form onSubmit={submit} className="tracker-alert-form">
        <label className="watch-field">
          <span className="gen-label">Your email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            required
          />
        </label>
        <button type="submit" className="watch-submit" disabled={busy || !email.trim() || !ready}>
          {busy ? "Sending..." : "Send confirmation link"}
        </button>
        <p className="watch-fineprint">
          No account. We email a confirmation link first, and the alert is one-shot.
        </p>
      </form>
      {result ? (
        <p className={`watch-result ${result.ok ? "watch-ok" : "watch-err"}`} role="status" aria-live="polite">
          {result.message}
        </p>
      ) : null}
    </section>
  );
}

function TrackerTable({ rows }: { rows: TrackerRow[] }) {
  return (
    <div className="results-wrap tracker-table-wrap">
      <table className="results tracker-table">
        <thead>
          <tr>
            <th>Resolver</th>
            <th>Location</th>
            <th>State</th>
            <th>Latency</th>
            <th>Answer</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.resolver} className={`tracker-row tracker-state-${row.state}`}>
              <td className="name">{row.resolver}</td>
              <td className="loc">{row.location.toLowerCase()}</td>
              <td><span className={`tracker-pill tracker-pill-${row.state}`}>{STATE_LABEL[row.state]}</span></td>
              <td className="col-latency">{row.latencyMs != null ? `${row.latencyMs} ms` : <span className="dim">-</span>}</td>
              <td className="col-answer">{renderRecords(row)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderRecords(row: TrackerRow) {
  if (row.status === "nxdomain") return <span className="dim">NXDOMAIN</span>;
  if (row.status === "timeout") return <span className="dim">timeout</span>;
  if (row.status === "servfail") return <span className="dim">SERVFAIL</span>;
  if (row.status === "error") return <span className="dim">{row.message ?? "error"}</span>;
  if (row.records.length === 0) return <span className="dim">(empty)</span>;
  return (
    <span className="tracker-records">
      {row.records.map((record, index) => (
        <code key={`${record}-${index}`}>{record}</code>
      ))}
    </span>
  );
}

function normalizeType(value: string): RecordType {
  const up = value.toUpperCase();
  return (RECORD_TYPES as readonly string[]).includes(up) ? up as RecordType : "A";
}

function normalizeHost(raw: string): string {
  return raw.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/\.$/, "");
}
